import {
  ProfileTemplateFieldMode as FieldMode,
  type ProfileTemplateFieldMode,
  type ProfileTemplateFieldPoliciesDto,
} from '@up2stay/up2promo-api-client';
import { cn } from '@up2stay/up2promo-ui';
import type { ReactNode } from 'react';

import type { ProfileTemplateFieldKey } from '@/core/lib/profile-template-fields';
import type { TemplateMemberValueColumn } from '@/core/lib/profile-template-member-values';

import type { TemplateMemberValuesEditorUiProps } from './template-policy-member-controls';
import { TemplatePolicyMemberControls } from './template-policy-member-controls';

interface TemplateFieldRowProps extends TemplateMemberValuesEditorUiProps {
  fieldKey: ProfileTemplateFieldKey;
  policies: ProfileTemplateFieldPoliciesDto;
  onPolicyChange: (
    key: ProfileTemplateFieldKey,
    mode: ProfileTemplateFieldMode,
  ) => void;
  canManage: boolean;
  policyLabels: { locked: string; free: string };
  label?: ReactNode;
  children: ReactNode;
  className?: string;
  memberValueColumn?: TemplateMemberValueColumn;
  memberDialogTitle?: string;
  memberDefaultValue?: string;
  memberInputMaxLength?: number;
  memberInputPlaceholder?: string;
}

export function TemplateFieldRow({
  fieldKey,
  policies,
  onPolicyChange,
  canManage,
  policyLabels,
  label,
  children,
  className,
  memberValueColumn,
  memberDialogTitle,
  memberDefaultValue,
  workspaceId,
  templateId,
  publishStatus,
  memberRoster,
  memberRosterLoading,
  hasAssignedTeams,
  memberValuesDict,
  memberInputMaxLength,
  memberInputPlaceholder,
}: TemplateFieldRowProps) {
  const column =
    memberValueColumn ??
    (fieldKey === 'jobTitle' ||
    fieldKey === 'company' ||
    fieldKey === 'location'
      ? {
          kind: 'field' as const,
          key: fieldKey,
          label: fieldKey,
        }
      : undefined);

  return (
    <div
      className={cn(
        'space-y-2 border-b border-border/40 pb-3 last:border-0 last:pb-0',
        className,
      )}
    >
      {label ? <div className="min-w-0">{label}</div> : null}

      <div
        className={cn(
          canManage &&
            'grid gap-2 xl:grid-cols-[minmax(0,1fr)_auto] xl:gap-x-4',
          canManage && (label ? 'xl:items-start' : 'xl:items-center'),
        )}
      >
        <div className="min-w-0 space-y-2">{children}</div>

        {canManage ? (
          <TemplatePolicyMemberControls
            value={policies[fieldKey] ?? FieldMode.FREE}
            onChange={(nextMode) => onPolicyChange(fieldKey, nextMode)}
            labels={policyLabels}
            memberValueColumn={column}
            memberDialogTitle={memberDialogTitle ?? column?.label}
            memberDefaultValue={memberDefaultValue}
            workspaceId={workspaceId}
            templateId={templateId}
            publishStatus={publishStatus}
            memberRoster={memberRoster}
            memberRosterLoading={memberRosterLoading}
            hasAssignedTeams={hasAssignedTeams}
            memberValuesDict={memberValuesDict}
            canManage={canManage}
            memberInputMaxLength={memberInputMaxLength}
            memberInputPlaceholder={memberInputPlaceholder}
            className="xl:shrink-0"
          />
        ) : null}
      </div>
    </div>
  );
}
